import { useState } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, CheckCircle2 } from "lucide-react";

const empty = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-24 lg:px-8" data-testid="contact-page">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-primary">
              Contact
            </p>
            <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
              Got a points question?
            </h1>
            <p className="mt-6 font-body leading-relaxed text-slate-600">
              Wondering how to use a stash of points, or spotted something off in
              a valuation? Send us a message and we'll get back to you.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <MessageSquare className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-heading font-semibold text-slate-900">We read every message</p>
                  <p className="font-body text-sm text-slate-500">Real replies, usually within a couple of days.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-heading font-semibold text-slate-900">Kept private</p>
                  <p className="font-body text-sm text-slate-500">Your details are stored securely and never sold.</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            {done ? (
              <div className="rounded-3xl border border-border bg-card p-10 text-center shadow-sm" data-testid="contact-success">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="mt-6 font-heading text-2xl font-bold text-slate-900">Message sent</h2>
                <p className="mt-3 font-body text-slate-600">Thanks for reaching out. We'll be in touch soon.</p>
                <Button variant="outline" className="mt-6 rounded-full border-slate-200 font-heading font-semibold text-slate-700 hover:border-primary hover:text-primary" onClick={() => { setForm(empty); setDone(false); }} data-testid="contact-reset-button">
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-6 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.2)] md:p-8" data-testid="contact-form">
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="c-name" className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Name</Label>
                    <Input id="c-name" data-testid="contact-input-name" required value={form.name} onChange={set("name")} placeholder="Your name" className="h-12 rounded-xl border-slate-200" />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</Label>
                    <Input id="c-email" data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="h-12 rounded-xl border-slate-200" />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Phone (optional)</Label>
                    <Input id="c-phone" data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="h-12 rounded-xl border-slate-200" />
                  </div>
                  <div>
                    <Label htmlFor="c-message" className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Message</Label>
                    <Textarea id="c-message" data-testid="contact-input-message" required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" className="rounded-xl border-slate-200" />
                  </div>
                  <Button type="submit" data-testid="contact-submit-button" disabled={submitting} className="w-full rounded-full bg-primary py-6 font-heading text-base font-bold text-primary-foreground shadow-[0_8px_24px_rgba(37,99,235,0.28)] transition-transform hover:-translate-y-0.5 hover:bg-blue-700">
                    {submitting ? "Sending…" : "Send Message"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
